(() => {
  if (customElements.get('gg-product-bundle')) return;
  customElements.define('gg-product-bundle', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.items = [...this.querySelectorAll('[data-bundle-item]')];
      this.submit = this.querySelector('[data-bundle-submit]');
      this.label = this.querySelector('[data-bundle-label]');
      this.total = this.querySelector('[data-bundle-total]');
      this.original = this.querySelector('[data-bundle-original]');
      this.badge = this.querySelector('[data-bundle-discount]');
      this.error = this.querySelector('[data-bundle-error]');
      this.formatMoney = cents => new Intl.NumberFormat(document.documentElement.lang || 'nl-NL', {
        style: 'currency', currency: this.dataset.currency || 'EUR'
      }).format(cents / 100);
      this.items.forEach(item => {
        item.querySelector('[data-bundle-toggle]').addEventListener('click', () => {
          if (this.busy || item.dataset.available !== 'true') return;
          this.setSelected(item, !item.classList.contains('is-selected'));
          this.selectionChanged();
        }, options);
        item.querySelector('[data-bundle-variant]')?.addEventListener('change', event => {
          const option = event.target.selectedOptions[0];
          this.setVariant(item, option.value, option.dataset.price, !option.disabled);
          this.selectionChanged();
        }, options);
      });
      const root = this.closest('.gg-pdp-main');
      root?.addEventListener('change', event => {
        if (!event.target.matches('.gg-pdp-main__variant input[name="id"]')) return;
        const input = event.target;
        const primary = this.items.find(item => item.dataset.primary === 'true');
        this.setVariant(primary, input.value, input.dataset.priceCents, !input.disabled);
        this.selectionChanged();
      }, options);
      this.submit.addEventListener('click', () => this.addToCart(), options);
      this.update();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
    }

    setSelected(item, selected) {
      item.classList.toggle('is-selected', selected);
      const toggle = item.querySelector('[data-bundle-toggle]');
      toggle.setAttribute('aria-pressed', String(selected));
      toggle.setAttribute('aria-label', `${selected ? 'Verwijder' : 'Selecteer'} ${item.dataset.productTitle}`);
    }

    setVariant(item, id, price, available) {
      if (!item) return;
      item.dataset.variantId = id;
      item.dataset.price = price;
      item.dataset.available = String(available);
      item.querySelector('[data-bundle-item-price]').textContent = this.formatMoney(Number(price));
      item.querySelector('[data-bundle-toggle]').disabled = !available;
      if (!available) this.setSelected(item, false);
    }

    selectionChanged() {
      this.added = false;
      this.error.hidden = true;
      this.update();
    }

    selectedItems() {
      return this.items.filter(item => item.classList.contains('is-selected') && item.dataset.available === 'true');
    }

    percentage(selected) {
      if (selected.length !== 2 || !this.dataset.discountCode || this.discountRejected) return 0;
      return Math.min(100, Math.max(0, Number(this.dataset.discountPercent) || 0));
    }

    update() {
      const selected = this.selectedItems();
      const percent = this.percentage(selected);
      const original = selected.reduce((sum, item) => sum + Number(item.dataset.price), 0);
      const discounted = selected.reduce((sum, item) => {
        const price = Number(item.dataset.price);
        return sum + price - Math.round(price * percent / 100);
      }, 0);
      this.total.textContent = this.formatMoney(discounted);
      this.original.textContent = this.formatMoney(original);
      this.original.hidden = percent === 0;
      this.badge.hidden = percent === 0;
      this.badge.textContent = `${percent.toLocaleString('nl-NL')}% korting`;
      this.submit.disabled = this.busy || this.added || selected.length === 0;
      this.label.textContent = this.added ? 'Toegevoegd aan winkelwagen' : selected.length === 0
        ? this.submit.dataset.emptyLabel : selected.length === 1
          ? this.submit.dataset.singleLabel : this.submit.dataset.multipleLabel;
    }

    async request(path, payload) {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}${path}`, payload ? {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      } : { headers: { Accept: 'application/json' }, cache: 'no-store' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || 'De winkelwagen kon niet worden bijgewerkt.');
      return data;
    }

    discountApplications(cart) {
      return [...(cart.cart_level_discount_applications || []),
        ...(cart.items || []).flatMap(item => [
          ...(item.line_level_discount_allocations || []).map(allocation => allocation.discount_application),
          ...(item.discounts || [])
        ])].filter(Boolean);
    }

    async addToCart() {
      const selected = this.selectedItems();
      if (this.busy || this.added || !selected.length) return;
      const percent = this.percentage(selected);
      const code = percent ? this.dataset.discountCode.trim() : '';
      const pair = selected.length === 2;
      const group = `${this.dataset.ownerId}:${this.dataset.companionId}`;
      const items = selected.map(item => ({ id: Number(item.dataset.variantId), quantity: 1,
        ...(pair ? { properties: { _gg_bundle: group } } : {}) }));
      const expectedTotal = selected.reduce((sum, item) => {
        const price = Number(item.dataset.price);
        return sum + price - Math.round(price * percent / 100);
      }, 0);
      this.busy = true;
      this.error.hidden = true;
      this.submit.setAttribute('aria-busy', 'true');
      this.querySelectorAll('[data-bundle-toggle], [data-bundle-variant]').forEach(control => {
        control.dataset.wasDisabled = String(control.disabled);
        control.disabled = true;
      });
      this.update();
      let cart, addedResponse, discountWarning = '';
      try {
        // Read before adding so a failed initial request cannot leave a partial operation.
        const previousCart = code ? await this.request('cart.js') : null;
        const sections = ['variant-added'];
        document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
        const sectionRequest = { sections: [...new Set(sections)].slice(0, 5), sections_url: window.location.pathname };
        addedResponse = await this.request('cart/add.js', { items, ...sectionRequest });
        this.added = true;
        if (code) {
          const previousCodes = [
            ...(previousCart.discount_codes || []).map(discount => discount.code),
            ...this.discountApplications(previousCart).filter(discount => discount.type === 'discount_code').map(discount => discount.title)
          ].filter(Boolean);
          const codes = [...new Set([...previousCodes, code])].join(',');
          try {
            cart = await this.request('cart/update.js', { discount: codes, ...sectionRequest });
            const applications = this.discountApplications(cart);
            const applied = (cart.discount_codes || []).some(discount => discount.code?.toLowerCase() === code.toLowerCase() && discount.applicable)
              || applications.some(discount => discount.title?.toLowerCase() === code.toLowerCase());
            const actualItems = items.map(item => cart.items?.find(line => Number(line.variant_id) === item.id && line.properties?._gg_bundle === group));
            const actualTotal = actualItems.reduce((sum, line) => sum + (line?.final_price ?? Infinity), 0);
            if (!applied || actualTotal > expectedTotal + 1) {
              discountWarning = 'De producten zijn toegevoegd, maar de bundelkorting kon niet volledig worden toegepast. Controleer de prijs in je winkelwagen voordat je afrekent.';
            }
          } catch {
            discountWarning = 'De producten zijn toegevoegd, maar de bundelkorting kon niet worden toegepast. Controleer je winkelwagen voordat je afrekent.';
          }
        }
        if (!cart) {
          cart = await this.request('cart.js');
          cart.sections = addedResponse.sections;
        }
        if (discountWarning) {
          this.discountRejected = true;
          this.error.textContent = discountWarning;
          this.error.hidden = false;
        }
        this.submit.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items: addedResponse.items || [], cart } }));
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart } }));
        if (!discountWarning && (window.themeVariables?.settings?.cartType === 'page' || window.themeVariables?.settings?.pageType === 'cart')) {
          window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
        }
      } catch (error) {
        this.error.textContent = this.added
          ? 'De producten zijn toegevoegd. Open je winkelwagen om de inhoud en de korting te controleren.'
          : error.message || 'De selectie kon niet worden toegevoegd.';
        this.error.hidden = false;
      } finally {
        this.busy = false;
        this.submit.removeAttribute('aria-busy');
        this.querySelectorAll('[data-bundle-toggle], [data-bundle-variant]').forEach(control => {
          control.disabled = control.dataset.wasDisabled === 'true';
        });
        this.update();
      }
    }
  });
})();
